import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Quill } from 'react-quill';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  InputGroup,
  ListGroup,
  Spinner,
  Alert,
  Breadcrumb,
  Tabs,
  Tab,
  Offcanvas
} from 'react-bootstrap';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Folder,
  FileText,
  Book,
  Star,
  Heart,
  Calendar,
  Flag,
  Bell,
  User,
  List as ListIcon,
  X as XIcon,
  Compass,
  Link2,
  Flame,
  Tent,
  Mountain,
  Trees,
  Binoculars,
  MapPin
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Erweiterter Image-Blot: Alle Bilder draggable
const ImageFormat = Quill.import('formats/image');
class DraggableImage extends ImageFormat {
  static create(value) {
    const node = super.create(value);
    node.setAttribute('draggable', true);
    return node;
  }
}
Quill.register(DraggableImage, true);
Quill.register('modules/imageResize', ImageResize);

// Ref für das aktuell gezogene Bild
const draggedImageRef = React.createRef();

// Benutzerdefinierter Image-Handler: Öffnet den File-Dialog, lädt zu Cloudinary hoch und fügt die URL ein
const imageHandler = function () {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = async () => {
    const file = input.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'PfadiInvetar'); // Upload-Preset anpassen
      try {
        const res = await fetch(
          'https://api.cloudinary.com/v1_1/dqng2dgmq/upload', // Cloudinary-Cloud-Namen anpassen
          { method: 'POST', body: formData }
        );
        const data = await res.json();
        if (data.secure_url) {
          const quill = this.quill;
          const range = quill.getSelection();
          quill.insertEmbed(range.index, 'image', data.secure_url);
          quill.setSelection(range.index + 1);
        } else {
          toast.error('Fehler beim Hochladen des Bildes.');
        }
      } catch (err) {
        console.error('Bild-Upload Fehler:', err);
        toast.error('Fehler beim Hochladen des Bildes.');
      }
    }
  };
};

const modules = {
  toolbar: {
    container: "#toolbar",
    handlers: { image: imageHandler }
  },
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize']
  }
};

// Hilfsfunktion: Interne Links im Editor-Inhalt parsen
const parseInternalLinks = (content) => {
  const regex = /\[\[([^\]]+)\]\]/g;
  const links = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    links.push(match[1].trim());
  }
  return links;
};

const startPage = {
  id: 'startpage',
  title: 'Startseite',
  content: `
    <h1>Willkommen im Sirius Wiki!</h1>
    <p>Dieses Wiki bietet Dir folgende Funktionen:</p>
    <ul>
      <li>Erstellen und Bearbeiten von Artikeln</li>
      <li>Kategorisierung der Inhalte</li>
      <li>Suche und Navigation</li>
      <li>Diskussionen und Kommentare</li>
    </ul>
    <p>Wähle einen Menüpunkt aus der Navigation oder erstelle eine neue Wiki-Seite, um zu beginnen.</p>
  `
};

const availableIcons = [
  "Folder", "FileText", "Book", "Tag", "Star", "Heart",
  "Calendar", "Flag", "Bell", "User",
  "Compass", "Link2", "Flame", "Tent",
  "Mountain", "Trees", "Binoculars", "MapPin"
];

const renderCategoryIcon = (iconName, color) => {
  const style = { color: color || 'inherit', verticalAlign: 'middle' };
  switch (iconName) {
    case 'Folder': return <Folder size={16} style={style} />;
    case 'FileText': return <FileText size={16} style={style} />;
    case 'Book': return <Book size={16} style={style} />;
    case 'Tag': return <Folder size={16} style={style} />;
    case 'Star': return <Star size={16} style={style} />;
    case 'Heart': return <Heart size={16} style={style} />;
    case 'Calendar': return <Calendar size={16} style={style} />;
    case 'Flag': return <Flag size={16} style={style} />;
    case 'Bell': return <Bell size={16} style={style} />;
    case 'User': return <User size={16} style={style} />;
    case 'Compass': return <Compass size={16} style={style} />;
    case 'Rope': return <Link2 size={16} style={style} />;
    case 'Flame': return <Flame size={16} style={style} />;
    case 'Tent': return <Tent size={16} style={style} />;
    case 'Mountain': return <Mountain size={16} style={style} />;
    case 'Trees': return <Trees size={16} style={style} />;
    case 'Binoculars': return <Binoculars size={16} style={style} />;
    case 'MapPin': return <MapPin size={16} style={style} />;
    default: return <Folder size={16} style={style} />;
  }
};

const checkAndDeleteCategory = async (categoryId) => {
  const q = query(
    collection(db, 'wiki_pages'),
    where('categoryId', '==', categoryId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    await deleteDoc(doc(db, 'wiki_categories', categoryId));
    toast.info('Kategorie automatisch gelöscht, da keine Seiten mehr vorhanden sind.');
  }
};

const handleDeleteCategory = async (categoryId) => {
  if (!window.confirm('Möchtest Du diese Kategorie wirklich löschen?')) return;
  try {
    await deleteDoc(doc(db, 'wiki_categories', categoryId));
    toast.success('Kategorie gelöscht');
  } catch (err) {
    console.error('Fehler beim Löschen der Kategorie:', err);
    toast.error('Fehler beim Löschen der Kategorie');
  }
};

const addAnchorsToHeadings = (htmlContent) => {
  return htmlContent.replace(/<h([1-6])([^>]*)>(.*?)<\/h\1>/g, (match, level, attrs, content) => {
    const anchor = content.replace(/\s+/g, '-').toLowerCase();
    return `<h${level}${attrs} id="${anchor}">${content}</h${level}>`;
  });
};

// Statische Styles außerhalb der Komponente
const wikiStyles = `
  body {
    background-color: #FDFDFE;
    font-family: 'Segoe UI', sans-serif;
  }
  .glass-sidebar {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-right: 1px solid rgba(0,0,0,0.1);
    min-height: 100vh;
    padding: 1rem;
  }
  .content-area {
    background: #fff;
    padding: 2rem;
    min-height: 100vh;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
  .rounded-shadow {
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  }
  .header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
  .header-bar h3 {
    font-weight: bold;
    margin: 0;
  }
  .btn-new-page {
    background-color: #333;
    border: none;
    color: #fff;
    padding: 0.5rem 1rem;
    border-radius: 6px;
  }
  .btn-new-page:hover {
    background-color: #222;
  }
  .btn-add-category {
    background: transparent;
    border: none;
    color: #28a745;
    font-size: 0.85rem;
    padding: 0.2rem 0.4rem;
  }
  .btn-add-category:hover {
    text-decoration: underline;
    background: transparent;
    color: #28a745;
  }
  .breadcrumb {
    background: none;
    padding-left: 0;
    margin-bottom: 1rem;
  }
  #toolbar {
    border: 1px solid #ccc;
    border-bottom: none;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background: #f8f9fa;
    padding: 0.5rem;
  }
  #toolbar button, #toolbar select {
    margin-right: 0.5rem;
  }
  .mobile-header {
    background: #e6eef5;
    border-bottom: 1px solid #ccc;
  }
  .mobile-new-btn {
    background-color: #333;
    color: #fff;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 4px;
    font-size: 1rem;
  }
  .mobile-new-btn:hover {
    background-color: #222;
  }
  .offcanvas-custom {
    background-color: #fff;
    padding: 1rem;
  }
  .offcanvas-custom .offcanvas-body {
    padding: 0;
  }
  .offcanvas-menu-header {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #ccc;
    padding-bottom: 0.5rem;
  }
  @media (max-width: 767.98px) {
    .content-area {
      padding: 1rem;
    }
  }
`;

// Memoisierte Sidebar-Komponente
const Sidebar = React.memo(({ searchTerm, filteredPages, onSelectPage, onSetSearchTerm }) => {
  return searchTerm ? (
    <ListGroup variant="flush" className="mb-3">
      {filteredPages.map(page => (
        <ListGroup.Item
          key={page.id}
          action
          onClick={() => {
            onSelectPage(page);
            onSetSearchTerm('');
          }}
        >
          <FileText size={16} style={{ marginRight: '0.5rem' }} />
          {page.title}
        </ListGroup.Item>
      ))}
    </ListGroup>
  ) : (
    <>
      <Button className="btn-add-category mb-3" onClick={() => onSelectPage(null)}>
        <Plus size={14} /> Neue Kategorie
      </Button>
      {/* Hier wird später der Kategorienbaum gerendert */}
    </>
  );
});

// Memoisierte MobileHeader-Komponente
const MobileHeader = React.memo(({ onShowSidebar, onNewPage }) => (
  <div
    className="mobile-header d-flex align-items-center justify-content-between px-3 py-3"
    style={{ backgroundColor: '#FDFDFE' }}
  >
    <h2 style={{ fontWeight: 'bold', margin: 0, fontSize: '2rem' }}>Wiki</h2>
    <div className="d-flex align-items-center">
      <Button variant="link" onClick={onShowSidebar} className="me-2">
        <ListIcon size={24} color="#333" />
      </Button>
      <Button className="mobile-new-btn" size="sm" onClick={onNewPage}>
        <Plus size={16} color="#fff" /> Neu
      </Button>
    </div>
  </div>
));

const WikiFull = () => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(startPage);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [showPageModal, setShowPageModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParent, setNewCategoryParent] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#000000');
  const [newCategoryOrder, setNewCategoryOrder] = useState(0);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('artikel');
  const quillRef = useRef(null);

  // Effekt: Drag & Drop im Editor (für Bilder)
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const handleDragStart = (e) => {
        if (e.target && e.target.tagName.toLowerCase() === 'img') {
          draggedImageRef.current = e.target;
        }
      };
      const handleDragOver = (e) => e.preventDefault();
      const handleDrop = (e) => {
        e.preventDefault();
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection();
        if (draggedImageRef.current && range) {
          const blot = Quill.find(draggedImageRef.current);
          if (blot) {
            const blotIndex = editor.getIndex(blot);
            editor.deleteText(blotIndex, 1);
            editor.insertEmbed(range.index, 'image', draggedImageRef.current.getAttribute('src'));
            editor.setSelection(range.index + 1);
          }
        }
        draggedImageRef.current = null;
      };
      editor.root.addEventListener('dragstart', handleDragStart);
      editor.root.addEventListener('dragover', handleDragOver);
      editor.root.addEventListener('drop', handleDrop);
      return () => {
        editor.root.removeEventListener('dragstart', handleDragStart);
        editor.root.removeEventListener('dragover', handleDragOver);
        editor.root.removeEventListener('drop', handleDrop);
      };
    }
  }, []);

  // Wiki-Seiten laden
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'wiki_pages'),
      (snapshot) => {
        const docs = snapshot.docs.map(docItem => ({
          id: docItem.id,
          ...docItem.data()
        }));
        setPages(docs);
        setIsLoading(false);
      },
      (err) => {
        console.error('Fehler beim Laden der Wiki-Seiten:', err);
        setError('Wiki-Seiten konnten nicht geladen werden.');
        toast.error('Fehler beim Laden der Wiki-Seiten. Prüfe Firestore-Regeln.');
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Kategorien laden (sortiert nach order)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'wiki_categories'),
      (snapshot) => {
        const cats = snapshot.docs.map(docItem => ({
          id: docItem.id,
          ...docItem.data()
        }));
        cats.sort((a, b) => (a.order || 0) - (b.order || 0));
        setCategories(cats);
      },
      (err) => {
        console.error('Fehler beim Laden der Kategorien:', err);
        toast.error('Fehler beim Laden der Kategorien.');
      }
    );
    return () => unsubscribe();
  }, []);

  // Kommentare laden (nur für Seiten außer Startseite)
  useEffect(() => {
    if (selectedPage && selectedPage.id !== 'startpage') {
      const q = query(
        collection(db, 'wiki_comments'),
        where('pageId', '==', selectedPage.id)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const commentData = snapshot.docs.map(docItem => ({
          id: docItem.id,
          ...docItem.data()
        }));
        setComments(commentData);
      });
      return () => unsubscribe();
    } else {
      setComments([]);
    }
  }, [selectedPage]);

  // Berechnung der gefilterten Wiki-Seiten
  const filteredPages = useMemo(() => {
    const lowerTerm = searchTerm.toLowerCase();
    return pages.filter(page =>
      (page.title && page.title.toLowerCase().includes(lowerTerm)) ||
      (page.content && page.content.toLowerCase().includes(lowerTerm))
    );
  }, [searchTerm, pages]);

  // Breadcrumb mittels useMemo
  const breadcrumb = useMemo(() => {
    if (selectedPage && selectedPage.id === 'startpage') {
      return ['Wiki', 'Startseite'];
    }
    let crumbs = ['Wiki'];
    if (selectedPage && selectedPage.categoryId) {
      let currentCat = categories.find(cat => cat.id.toString() === selectedPage.categoryId.toString());
      const catCrumbs = [];
      while (currentCat) {
        catCrumbs.unshift(currentCat.name);
        if (currentCat.parentId) {
          currentCat = categories.find(cat => cat.id.toString() === currentCat.parentId.toString());
        } else {
          currentCat = null;
        }
      }
      crumbs = crumbs.concat(catCrumbs);
    }
    if (selectedPage) {
      crumbs.push(selectedPage.title);
    }
    return crumbs;
  }, [selectedPage, categories]);

  // Stabile Callbacks
  const handleAddComment = useCallback(async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await addDoc(collection(db, 'wiki_comments'), {
        pageId: selectedPage.id,
        content: newComment,
        createdAt: serverTimestamp()
      });
      toast.success('Kommentar hinzugefügt');
      setNewComment('');
    } catch (err) {
      console.error('Fehler beim Hinzufügen des Kommentars:', err);
      toast.error('Fehler beim Hinzufügen des Kommentars');
    }
  }, [newComment, selectedPage]);

  const handleDeleteComment = useCallback(async (commentId) => {
    if (!window.confirm('Möchtest Du diesen Kommentar wirklich löschen?')) return;
    try {
      await deleteDoc(doc(db, 'wiki_comments', commentId));
      toast.success('Kommentar gelöscht');
    } catch (err) {
      console.error('Fehler beim Löschen des Kommentars:', err);
      toast.error('Fehler beim Löschen des Kommentars');
    }
  }, []);

  const openNewPageModal = useCallback(() => {
    setEditingPage(null);
    setTitle('');
    setContent('');
    setSelectedCategoryId('');
    setImages([]);
    setPreviewMode(false);
    setShowPageModal(true);
  }, []);

  const openEditPageModal = useCallback((page) => {
    setEditingPage(page);
    setTitle(page.title || '');
    setContent(page.content || '');
    setSelectedCategoryId(page.categoryId || '');
    setImages(page.images || []);
    setPreviewMode(false);
    setShowPageModal(true);
  }, []);

  const closePageModal = useCallback(() => {
    setShowPageModal(false);
    setEditingPage(null);
    setTitle('');
    setContent('');
    setSelectedCategoryId('');
    setImages([]);
    setPreviewMode(false);
  }, []);

  const handleSavePage = useCallback(async (e) => {
    e.preventDefault();
    const pageData = {
      title,
      content,
      categoryId: selectedCategoryId,
      images,
      updatedAt: serverTimestamp()
    };
    try {
      if (editingPage) {
        await updateDoc(doc(db, 'wiki_pages', editingPage.id), pageData);
        toast.success('Seite aktualisiert');
      } else {
        pageData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'wiki_pages'), pageData);
        toast.success('Seite erstellt');
      }
      closePageModal();
    } catch (err) {
      console.error('Fehler beim Speichern der Wiki-Seite:', err);
      toast.error('Fehler beim Speichern der Wiki-Seite');
    }
  }, [title, content, selectedCategoryId, images, editingPage, closePageModal]);

  const handleDeletePage = useCallback(async (id) => {
    const pageToDelete = pages.find(p => p.id === id);
    if (!window.confirm('Möchtest Du diese Seite wirklich löschen?')) return;
    try {
      await deleteDoc(doc(db, 'wiki_pages', id));
      toast.success('Seite gelöscht');
      if (selectedPage && selectedPage.id === id) {
        setSelectedPage(startPage);
      }
      if (pageToDelete && pageToDelete.categoryId) {
        await checkAndDeleteCategory(pageToDelete.categoryId);
      }
    } catch (err) {
      console.error('Fehler beim Löschen der Wiki-Seite:', err);
      toast.error('Fehler beim Löschen der Wiki-Seite');
    }
  }, [pages, selectedPage]);

  const handleImageUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'YOUR_UPLOAD_PRESET'); // Bitte anpassen
    try {
      const res = await fetch(
        'https://api.cloudinary.com/v1_1/YOUR_CLOUD_NAME/image/upload', // Bitte anpassen
        { method: 'POST', body: formData }
      );
      const data = await res.json();
      if (data.secure_url) {
        setImages(prev => [...prev, data.secure_url]);
        toast.success('Bild hochgeladen');
      } else {
        toast.error('Fehler beim Bild-Upload');
      }
    } catch (err) {
      console.error('Fehler beim Bild-Upload:', err);
      toast.error('Fehler beim Bild-Upload');
    }
  }, []);

  const handleFileChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
    }
  }, [handleImageUpload]);

  const openCategoryModal = useCallback((category = null) => {
    if (category) {
      setEditingCategory(category);
      setNewCategoryName(category.name || '');
      setNewCategoryParent(category.parentId || '');
      setNewCategoryIcon(category.icon || '');
      setNewCategoryColor(category.color || '#000000');
      setNewCategoryOrder(category.order || 0);
    } else {
      setEditingCategory(null);
      setNewCategoryName('');
      setNewCategoryParent('');
      setNewCategoryIcon('');
      setNewCategoryColor('#000000');
      setNewCategoryOrder(categories.length);
    }
    setShowCategoryModal(true);
  }, [categories]);

  const closeCategoryModal = useCallback(() => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setNewCategoryName('');
    setNewCategoryParent('');
    setNewCategoryIcon('');
    setNewCategoryColor('#000000');
    setNewCategoryOrder(0);
  }, []);

  const handleSaveCategory = useCallback(async (e) => {
    e.preventDefault();
    if (newCategoryName.trim() === '') {
      toast.error('Bitte gib einen Kategorienamen ein.');
      return;
    }
    const catData = {
      name: newCategoryName.trim(),
      icon: newCategoryIcon,
      color: newCategoryColor,
      parentId: newCategoryParent.trim() ? newCategoryParent : null,
      order: Number(newCategoryOrder),
      updatedAt: serverTimestamp()
    };
    try {
      if (editingCategory) {
        await updateDoc(doc(db, 'wiki_categories', editingCategory.id), catData);
        toast.success('Kategorie aktualisiert');
      } else {
        catData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'wiki_categories'), catData);
        toast.success('Kategorie hinzugefügt');
      }
      closeCategoryModal();
    } catch (err) {
      console.error('Fehler beim Speichern der Kategorie:', err);
      toast.error('Fehler beim Speichern der Kategorie');
    }
  }, [newCategoryName, newCategoryIcon, newCategoryColor, newCategoryParent, newCategoryOrder, editingCategory, closeCategoryModal]);

  const handleDragEnd = useCallback(async (result) => {
    if (!result.destination) return;
    const topCategories = categories.filter(cat => !cat.parentId);
    const reordered = Array.from(topCategories);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    try {
      for (let i = 0; i < reordered.length; i++) {
        await updateDoc(doc(db, 'wiki_categories', `${reordered[i].id}`), { order: i });
      }
      const otherCats = categories.filter(cat => cat.parentId);
      setCategories([...reordered, ...otherCats]);
      toast.success('Reihenfolge aktualisiert');
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Reihenfolge:', err);
      toast.error('Fehler beim Aktualisieren der Reihenfolge');
    }
  }, [categories]);

  // Kategorienbaum rendern
  const renderCategoryTree = useCallback(() => {
    const topCategories = categories.filter(cat => !cat.parentId);
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories">
          {(provided) => (
            <ListGroup variant="flush" ref={provided.innerRef} {...provided.droppableProps}>
              {topCategories.map((top, index) => (
                <Draggable key={top.id} draggableId={`${top.id}`} index={index}>
                  {(providedDraggable) => (
                    <div ref={providedDraggable.innerRef} {...providedDraggable.draggableProps}>
                      <ListGroup.Item
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '0.3rem 0'
                        }}
                        onClick={() => {
                          setSearchTerm('');
                          const catPages = pages.filter(p => p.categoryId && p.categoryId.toString() === top.id.toString());
                          if (catPages.length) {
                            setSelectedPage(catPages[0]);
                          }
                          setShowMobileSidebar(false);
                        }}
                        {...providedDraggable.dragHandleProps}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <span style={{ marginRight: '0.5rem' }}>
                              {renderCategoryIcon(top.icon, top.color)}
                            </span>
                            <strong>{top.name}</strong>
                          </div>
                          <div>
                            <span
                              onClick={(e) => { e.stopPropagation(); openCategoryModal(top); }}
                              style={{ cursor: 'pointer', marginRight: '0.5rem' }}
                            >
                              <Edit size={14} />
                            </span>
                            <span
                              onClick={(e) => { e.stopPropagation(); handleDeleteCategory(top.id); }}
                              style={{ cursor: 'pointer' }}
                            >
                              <Trash2 size={14} />
                            </span>
                          </div>
                        </div>
                      </ListGroup.Item>
                      {renderPagesForCategory(top.id)}
                      {categories.filter(cat => cat.parentId && cat.parentId.toString() === top.id.toString()).map(sub => (
                        <React.Fragment key={sub.id}>
                          <ListGroup.Item
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: '0.3rem 0 0.3rem 20px'
                            }}
                            onClick={() => {
                              setSearchTerm('');
                              const subPages = pages.filter(p => p.categoryId && p.categoryId.toString() === sub.id.toString());
                              if (subPages.length) {
                                setSelectedPage(subPages[0]);
                              }
                              setShowMobileSidebar(false);
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <span style={{ marginRight: '0.5rem' }}>{renderCategoryIcon(sub.icon, sub.color)}</span>
                                – <strong>{sub.name}</strong>
                              </div>
                              <div>
                                <span
                                  onClick={(e) => { e.stopPropagation(); openCategoryModal(sub); }}
                                  style={{ cursor: 'pointer', marginRight: '0.5rem' }}
                                >
                                  <Edit size={14} />
                                </span>
                                <span
                                  onClick={(e) => { e.stopPropagation(); handleDeleteCategory(sub.id); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <Trash2 size={14} />
                                </span>
                              </div>
                            </div>
                          </ListGroup.Item>
                          {renderPagesForCategory(sub.id, 2)}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ListGroup>
          )}
        </Droppable>
      </DragDropContext>
    );
  }, [categories, pages, handleDragEnd, openCategoryModal]);

  const renderPagesForCategory = useCallback((catId, indent = 1) => {
    const pagesForCat = pages.filter(p => p.categoryId && p.categoryId.toString() === catId.toString());
    if (!pagesForCat.length) return null;
    return (
      <ListGroup variant="flush" style={{ marginLeft: `${indent * 10}px` }}>
        {pagesForCat.map(page => (
          <ListGroup.Item
            action
            style={{ background: 'transparent', border: 'none', padding: '0.2rem 0' }}
            onClick={() => {
              setSelectedPage(page);
              setShowMobileSidebar(false);
            }}
            key={page.id}
          >
            <FileText size={16} style={{ marginRight: '0.5rem' }} />
            {page.title}
          </ListGroup.Item>
        ))}
      </ListGroup>
    );
  }, [pages]);

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p>Lade Wiki-Seiten …</p>
      </Container>
    );
  }
  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Fehler</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Neu laden
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <style type="text/css">{wikiStyles}</style>
      <ToastContainer position="bottom-right" />
      <Container fluid>
        <Row>
          {/* Desktop Sidebar */}
          <Col md={3} className="glass-sidebar d-none d-md-block">
            <div className="header-bar">
              <h3>Wiki</h3>
              <Button className="btn-new-page" onClick={openNewPageModal}>
                <Plus size={16} /> Neu
              </Button>
            </div>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <Search size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Suche …"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Sidebar
              searchTerm={searchTerm}
              filteredPages={filteredPages}
              onSelectPage={setSelectedPage}
              onSetSearchTerm={setSearchTerm}
            />
            {renderCategoryTree()}
          </Col>

          {/* Mobile Header */}
          <Col xs={12} className="d-md-none">
            <MobileHeader
              onShowSidebar={() => setShowMobileSidebar(true)}
              onNewPage={openNewPageModal}
            />
          </Col>

          {/* Hauptinhalt */}
          <Col xs={12} md={9} className="content-area">
            <Breadcrumb>
              {breadcrumb.map((crumb, idx) => (
                <Breadcrumb.Item key={idx} active>
                  {crumb}
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <h2>{selectedPage.title}</h2>
                {selectedPage.id !== 'startpage' && (
                  <div>
                    <Button variant="outline-secondary" onClick={() => openEditPageModal(selectedPage)}>
                      <Edit size={16} />
                    </Button>{' '}
                    <Button variant="outline-danger" onClick={() => handleDeletePage(selectedPage.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                )}
              </div>
              {selectedPage.updatedAt && (
                <p style={{ fontSize: '0.7rem', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                  Zuletzt bearbeitet: {new Date(selectedPage.updatedAt.toDate()).toLocaleString()}
                </p>
              )}
            </div>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
              <Tab eventKey="artikel" title="Artikel">
                <div dangerouslySetInnerHTML={{ __html: addAnchorsToHeadings(selectedPage.content) }}></div>
                {selectedPage.images && selectedPage.images.length > 0 && (
                  <div className="mt-3">
                    {selectedPage.images.map((imgUrl, idx) => (
                      <img
                        key={idx}
                        src={imgUrl}
                        alt={`Bild ${idx + 1}`}
                        style={{ maxWidth: '100%', marginBottom: '1rem', borderRadius: '8px' }}
                      />
                    ))}
                  </div>
                )}
              </Tab>
              {selectedPage.id !== 'startpage' && (
                <Tab eventKey="diskussion" title="Diskussion">
                  <Form onSubmit={handleAddComment}>
                    <Form.Group>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Neuer Kommentar …"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                      />
                    </Form.Group>
                    <Button type="submit" className="mt-2">Kommentar hinzufügen</Button>
                  </Form>
                  <ListGroup className="mt-3">
                    {comments
                      .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))
                      .map((comment) => (
                        <ListGroup.Item key={comment.id}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>{comment.content}</div>
                            <span onClick={() => handleDeleteComment(comment.id)} style={{ cursor: 'pointer' }}>
                              <Trash2 size={14} />
                            </span>
                          </div>
                          <small className="text-muted">
                            {comment.createdAt ? new Date(comment.createdAt.toDate()).toLocaleString() : ''}
                          </small>
                        </ListGroup.Item>
                      ))}
                  </ListGroup>
                </Tab>
              )}
            </Tabs>
          </Col>
        </Row>
      </Container>

      {/* Offcanvas für mobile Navigation */}
      <Offcanvas show={showMobileSidebar} onHide={() => setShowMobileSidebar(false)} placement="end" className="offcanvas-custom">
        <Offcanvas.Body>
          <div className="offcanvas-menu-header">
            <span>Navigation</span>
            <Button variant="link" onClick={() => setShowMobileSidebar(false)}>
              <XIcon size={24} color="#333" />
            </Button>
          </div>
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <Search size={16} color="#333" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Suche …"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Sidebar
            searchTerm={searchTerm}
            filteredPages={filteredPages}
            onSelectPage={setSelectedPage}
            onSetSearchTerm={setSearchTerm}
          />
          {renderCategoryTree()}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Modal: Wiki-Seite erstellen / bearbeiten */}
      <Modal show={showPageModal} onHide={closePageModal} size="lg" className="rounded-shadow">
        <Modal.Header closeButton>
          <Modal.Title>{editingPage ? 'Wiki-Seite bearbeiten' : 'Neue Wiki-Seite erstellen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSavePage}>
            <Form.Group className="mb-3">
              <Form.Label>Titel</Form.Label>
              <Form.Control type="text" required value={title} onChange={(e) => setTitle(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Inhalt</Form.Label>
              <div className="d-flex justify-content-end mb-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setPreviewMode(!previewMode)}>
                  {previewMode ? 'Bearbeiten' : 'Vorschau'}
                </Button>
              </div>
              {previewMode ? (
                <div className="border p-2" dangerouslySetInnerHTML={{ __html: content }} />
              ) : (
                <>
                  <div id="toolbar">
                    <select className="ql-header">
                      <option value="1">Überschrift 1</option>
                      <option value="2">Überschrift 2</option>
                      <option defaultValue>Normal</option>
                    </select>
                    <button className="ql-bold"></button>
                    <button className="ql-italic"></button>
                    <button className="ql-underline"></button>
                    <button className="ql-list" value="ordered"></button>
                    <button className="ql-list" value="bullet"></button>
                    <button className="ql-blockquote"></button>
                    <button className="ql-link"></button>
                    <button className="ql-image"></button>
                  </div>
                  <ReactQuill
                    ref={quillRef}
                    value={content}
                    onChange={setContent}
                    theme="snow"
                    modules={modules}
                  />
                </>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Kategorie</Form.Label>
              <Form.Select value={selectedCategoryId} onChange={(e) => setSelectedCategoryId(e.target.value)}>
                <option value="">Keine Kategorie</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Bild hochladen (optional)</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
              {images && images.length > 0 && (
                <ListGroup className="mt-2">
                  {images.map((imgUrl, idx) => (
                    <ListGroup.Item key={idx}>
                      <a href={imgUrl} target="_blank" rel="noreferrer">{imgUrl}</a>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={closePageModal}>Abbrechen</Button>
              <Button variant="primary" type="submit">{editingPage ? 'Aktualisieren' : 'Erstellen'}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal: Kategorie erstellen / bearbeiten */}
      <Modal show={showCategoryModal} onHide={closeCategoryModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie hinzufügen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveCategory}>
            <Form.Group className="mb-3">
              <Form.Label>Kategoriename</Form.Label>
              <Form.Control
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Bitte Kategorienamen eingeben"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Elternkategorie (optional)</Form.Label>
              <Form.Select value={newCategoryParent} onChange={(e) => setNewCategoryParent(e.target.value)}>
                <option value="">Keine</option>
                {categories.filter(cat => !cat.parentId).map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Icon auswählen</Form.Label>
              <Form.Select value={newCategoryIcon} onChange={(e) => setNewCategoryIcon(e.target.value)}>
                <option value="">Bitte Icon wählen</option>
                {availableIcons.map((icon, idx) => (
                  <option key={idx} value={icon}>
                    {icon}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Farbe auswählen</Form.Label>
              <Form.Control type="color" value={newCategoryColor} onChange={(e) => setNewCategoryColor(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reihenfolge</Form.Label>
              <Form.Control
                type="number"
                value={newCategoryOrder}
                onChange={(e) => setNewCategoryOrder(e.target.value)}
                min="0"
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="primary" type="submit">
                {editingCategory ? 'Aktualisieren' : 'Hinzufügen'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default WikiFull;